import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import s from "../modules/Profile.module.css";
import { useT, useI18nReady } from "../components/TextProvider";

/** ——— Endpoints ——— */
const USERS_ENDPOINT = process.env.REACT_APP_USERS_ENDPOINT || "https://apitr.subxonqulov.uz/api/users/";
const USERS_CHANGEINFO = "https://apitr.subxonqulov.uz/api/v1/dj-rest-auth/";
const ME_ENDPOINT = process.env.REACT_APP_ME_ENDPOINT || "https://apitr.subxonqulov.uz/api/users/me/";
const VAZIFALAR_ENDPOINT = process.env.REACT_APP_VAZIFALAR_ENDPOINT || "https://apitr.subxonqulov.uz/api/vazifalar/";
const FANLAR_ENDPOINT = "https://apitr.subxonqulov.uz/api/fanlar/";
const PASSWORD_CHANGE_ENDPOINT = process.env.REACT_APP_PASSWORD_CHANGE_ENDPOINT || "https://apitr.subxonqulov.uz/api/auth/password/change/";
const AIKEYS_ENDPOINT = process.env.REACT_APP_AIKEYS_ENDPOINT || "https://apitr.subxonqulov.uz/api/aikeys/";
const USER_ROLES_ENDPOINT = process.env.REACT_APP_USER_ROLES_ENDPOINT || "https://apitr.subxonqulov.uz/api/user-roles/";
const YOZMA_SAVOLLAR_ENDPOINT = process.env.REACT_APP_YOZMA_SAVOLLAR_ENDPOINT || "https://apitr.subxonqulov.uz/api/kurs-yozma-savollar/";

/** ——— Auth helpers (синхронизировано с Navigation) ——— */
function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

function clearAuthStorage() {
  const clear = (storage) => {
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith("auth:")) keys.push(k);
    }
    keys.forEach((k) => storage.removeItem(k));
  };
  clear(window.localStorage);
  clear(window.sessionStorage);
}

function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") || s.getItem("auth:token") || s.getItem("auth:key") ||
    s.getItem("auth:jwt") || s.getItem("auth:auth_token") || null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}

function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forced = process.env.REACT_APP_AUTH_SCHEME;
  if (forced) return { Authorization: `${forced} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

async function fetchJSON(url, options = {}) {
  const res = await fetch(url, options);
  let data = null;
  try { data = await res.json(); } catch {}
  return { ok: res.ok, status: res.status, data };
}

/** ——— Utils ——— */
function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]?.toUpperCase() || "").join("") || "U";
}
function maskKey(k = "") {
  if (!k) return "";
  if (k.length <= 8) return "••••";
  return k.slice(0, 4) + "••••••••" + k.slice(-4);
}
function savePref(key, val) {
  try { localStorage.setItem(key, String(val)); } catch {}
}
function readPref(key, fallback) {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}

/** ——— Profile Page ——— */
export default function Profile() {
  const t = useT();
  const ready = useI18nReady();
  const navigate = useNavigate();

  const token = useMemo(() => getAnyTokenFromStorage(), []);
  const cachedUser = useMemo(() => getStoredUser(), []);
  
  const [me, setMe] = useState(cachedUser);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState({ full_name: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");

  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState("");

  const [vazifalar, setVazifalar] = useState([]);
  const [tCount, setTCount] = useState(0);
  const [fanCount, setFanCount] = useState(0);
  const [aikeys, setAikeys] = useState([]);
  const [isTrainer, setIsTrainer] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);

  /** 1. Загрузка данных */
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      setLoading(true);
      setError("");
      try {
        let meData = null;
        if (ME_ENDPOINT) {
          const r = await fetchJSON(ME_ENDPOINT, {
            headers: { ...makeAuthHeader(token) },
            credentials: "include",
            signal: ac.signal,
          });
          if (r.ok) meData = r.data;
        }
        if (!meData && cachedUser?.id) {
          const r = await fetchJSON(`${USERS_ENDPOINT}${cachedUser.id}/`, {
            headers: { ...makeAuthHeader(token) },
            credentials: "include",
            signal: ac.signal,
          });
          if (r.ok) meData = r.data;
        }
        if (!meData && cachedUser) meData = cachedUser;

        if (meData) {
          setMe(meData);
          setForm({
            full_name: meData.full_name ?? meData.name ?? meData.username ?? "",
            email: meData.email ?? "",
          });
        }

        // Загружаем список всех предметов и считаем, сколько из них принадлежит текущему пользователю
        const rFanlar = await fetchJSON(FANLAR_ENDPOINT, {
          headers: { ...makeAuthHeader(token) },
          credentials: "include",
          signal: ac.signal,
        });
        
        if (rFanlar.ok && Array.isArray(rFanlar.data) && meData?.id) {
          const userFans = rFanlar.data.filter(fan => Number(fan.user) === Number(meData.id));
          setFanCount(userFans.length);
        }

        if (meData?.id) {
          const r2 = await fetchJSON(`${VAZIFALAR_ENDPOINT}?user=${encodeURIComponent(meData.id)}`, {
            headers: { ...makeAuthHeader(token) },
            credentials: "include",
            signal: ac.signal,
          });
          if (r2.ok && Array.isArray(r2.data)) {
            setVazifalar(r2.data);
          }

          // Загружаем вопросы пользователя
          const r3 = await fetchJSON(YOZMA_SAVOLLAR_ENDPOINT, {
            headers: { ...makeAuthHeader(token) },
            credentials: "include",
            signal: ac.signal,
          });
          if (r3.ok) {
            let arr = Array.isArray(r3.data) ? r3.data : Array.isArray(r3.data?.results) ? r3.data.results : [];
            // Фильтруем по пользователю - проверяем item.user и item.kurs.user
            arr = arr.filter((item) => {
              const itemUserId = Number(item?.user) || Number(item?.kurs?.user);
              return itemUserId === Number(meData.id);
            });
            setQuestionCount(arr.length);
          }
        }

        const r4 = await fetchJSON(AIKEYS_ENDPOINT, {
          headers: { ...makeAuthHeader(token) },
          credentials: "include",
          signal: ac.signal,
        });
        if (r4.ok && Array.isArray(r4.data)) setAikeys(r4.data);

      } catch (e) {
        if (!ac.signal.aborted) setError("Profilni yuklashda xatolik yuz berdi");
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [token, cachedUser]);

  /** 2. Проверка роли тренера */
  useEffect(() => {
    if (!me?.id) {
      setIsTrainer(false);
      return;
    }
    let abort = false;
    (async () => {
      try {
        const r = await fetchJSON(USER_ROLES_ENDPOINT, {
          headers: { ...makeAuthHeader(token) },
          credentials: "include",
        });
        const list = Array.isArray(r.data) ? r.data : [];
        const mine = list.some(row => Number(row?.user) === Number(me.id) && Number(row?.role) === 2);
        if (!abort) setIsTrainer(mine);
      } catch {
        if (!abort) setIsTrainer(false);
      }
    })();
    return () => { abort = true; };
  }, [me?.id, token]);

  /** 3. Превью аватара */
  useEffect(() => {
    if (!avatarFile) { setAvatarPreview(""); return; }
    const url = URL.createObjectURL(avatarFile);
    setAvatarPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  /** Handlers */
  function onChange(e) {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  }

  function onPwdChange(e) {
    const { name, value } = e.target;
    setPwd(p => ({ ...p, [name]: value }));
  }

  async function handleSaveProfile() {
    if (!me?.id) return;
    console.log(me.id)
    setSaving(true);
    setError("");
    try {
      let ok = false, data = null;
      if (avatarFile) {
        const fd = new FormData();
        fd.append("full_name", form.full_name || "");
        fd.append("email", form.email || "");
        fd.append("avatar", avatarFile);
        const r = await fetch(`${USERS_ENDPOINT}${me.id}/`, {
          method: "PATCH",
          headers: { ...makeAuthHeader(token) },
          body: fd,
          credentials: "include",
        });
        ok = r.ok; try { data = await r.json(); } catch {}
      } else {
        const r = await fetchJSON(`${USERS_CHANGEINFO}${me.id}/`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...makeAuthHeader(token) },
          body: JSON.stringify({ full_name: form.full_name || "", email: form.email || "" }),
          credentials: "include",
        });
        ok = r.ok; data = r.data;
      }
      if (!ok) throw new Error("Profilni saqlashda xatolik yuz berdi");
      setMe(prev => ({ ...prev, ...data }));
    } catch (e) {
      setError(e?.message || "Profilni saqlashda xatolik yuz berdi");
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword() {
    if (!pwd.current_password || !pwd.new_password || !pwd.confirm) {
      setPwdMsg("Iltimos, barcha maydonlarni to'ldiring"); return;
    }
    if (pwd.new_password !== pwd.confirm) {
      setPwdMsg("Yangi parollar bir-biriga mos kelmayapti."); return;
    }
    setPwdSaving(true);
    setPwdMsg("");
    try {
      const r = await fetchJSON(PASSWORD_CHANGE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...makeAuthHeader(token) },
        body: JSON.stringify({ current_password: pwd.current_password, new_password: pwd.new_password }),
        credentials: "include",
      });
      if (!r.ok) throw new Error(r.data?.detail || "Parolni o'zgartirib bo'lmadi");
      setPwd({ current_password: "", new_password: "", confirm: "" });
      setPwdMsg("Parol yangilandi");
      setTimeout(() => setPwdMsg(""), 1500);
    } catch (e) {
      setPwdMsg(e?.message || "Parolni o'zgartirishda xatolik yuz berdi");
    } finally {
      setPwdSaving(false);
    }
  }

  /** КНОПКА ВЫХОДА (Исправлено) */
  function handleLogout() {
    // 1. Очищаем локальное хранилище
    clearAuthStorage();
    // 2. Уведомляем другие компоненты (Navigation) об изменениях
    window.dispatchEvent(new Event("auth:changed"));
    // 3. Перенаправляем на страницу входа
    navigate("/login", { replace: true });
  }

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <div className={s.userBlock}>
          <div className={s.avatar}>
            {avatarPreview ? (
              <img src={avatarPreview} alt="avatar" />
            ) : me?.avatar ? (
              <img src={me.avatar} alt="avatar" />
            ) : (
              <span className={s.avatarFallback}>{initials(me?.full_name || me?.username)}</span>
            )}
          </div>
          <div>
            <h1 className={s.title}>{me?.full_name || me?.username || t('profile')}</h1>
            <div className={s.subtitle}>{me?.email || "—"}</div>
          </div>
        </div>

        <div className={s.headerActions}>
          {isTrainer && (
            <>
              <Link to="/MeningFanlarim" className={s.btnGhost}>{t('mySubs')}</Link>
              <Link to="/TrainerMy" className={s.btnGhost}>{t('myTren')}</Link>
            </>
          )}
          <button className={s.btnGhost} onClick={handleLogout}>{t('Logout')}</button>
        </div>
      </div>

      {error && <div className={s.error}>{error}</div>}
      {loading && <div className={s.loading}>Yuklanmoqda…</div>}

      {!loading && (
        <>
          {/* Личные данные */}
          <div className={s.card}>
            <div className={s.cardHeader}>
              <div className={s.cardTitle}>{t('shaxsiy')}</div>
              <div className={s.cardHint}>{t('ismPochta')}</div>
            </div>
            <div className={s.grid2}>
              <div className={s.field}>
                <label className={s.label}>{t('tuliqIsm')}</label>
                <div className={s.staticValue}>{me?.full_name || me?.username || "—"}</div>
              </div>
              <div className={s.field}>
                <label className={s.label}>{t('pochta')}</label>
                <div className={s.staticValue}>{me?.email || "—"}</div>
              </div>
            </div>
          </div>

          {/* Статистика уроков */}
          <div className={s.card}>
            <div className={s.cardHeader}>
              <div className={s.cardTitle}>Statistika</div>
              <div className={s.cardHint}>Sizning o'quv jarayoningiz</div>
            </div>
            <div className={s.statsRow}>
              <div className={s.statBox}>
                <div className={s.statVal}>{fanCount}</div>
                <div className={s.statLabel}>Fanlar</div>
              </div>
              <div className={s.statBox}>
                <div className={s.statVal}>{vazifalar.length}</div>
                <div className={s.statLabel}>Mashqlar</div>
              </div>
              <div className={s.statBox}>
                <div className={s.statVal}>{questionCount}</div>
                <div className={s.statLabel}>Savollar</div>
              </div>
            </div>
          </div>

          {/* Статистика тренера */}
          {/* {isTrainer && (
            <div className={s.card}>
              <div className={s.cardHeader}>
                <div className={s.cardTitle}>{t('Faollik')}</div>
                <div className={s.cardHint}>{t('kontentStat')}</div>
              </div>
              <div className={s.statsRow}>
                <div className={s.statBox}>
                  <div className={s.statVal}>{vazifalar.length}</div>
                  <div className={s.statLabel}>{t('Mashqlar')}</div>
                  <Link className={s.statLink} to="/TrainerMy">{t('ochish')}</Link>
                </div>
                <div className={s.statBox}>
                  <div className={s.statVal}>{tCount}</div>
                  <div className={s.statLabel}>{t('Tushunchalar')}</div>
                  <Link className={s.statLink} to="/MyDescriptions">{t('ochish')}</Link>
                </div>
              </div>
            </div>
          )} */}

          {/* Безопасность */}
          {/* <div className={s.card}>
            <div className={s.cardHeader}>
              <div className={s.cardTitle}>{t('Xavfsizlik')}</div>
              <div className={s.cardHint}>{t('parolUzgartirish')}</div>
            </div>
            <div className={s.grid3}>
              <div className={s.field}>
                <label className={s.label}>{t('joriyParol')}</label>
                <input className={s.input} type="password" name="current_password" value={pwd.current_password} onChange={onPwdChange} placeholder="••••••••" />
              </div>
              <div className={s.field}>
                <label className={s.label}>{t('yangiParol')}</label>
                <input className={s.input} type="password" name="new_password" value={pwd.new_password} onChange={onPwdChange} placeholder="Kuchli parol" />
              </div>
              <div className={s.field}>
                <label className={s.label}>{t('parolnitakrorlang')}</label>
                <input className={s.input} type="password" name="confirm" value={pwd.confirm} onChange={onPwdChange} placeholder="Yana bir marta" />
              </div>
            </div>
            {pwdMsg && <div className={s.info}>{pwdMsg}</div>}
            <div className={s.actions}>
              <button className={s.btnDanger} onClick={handleChangePassword} disabled={pwdSaving}>
                {pwdSaving ? "Yangilanmoqda…" : "Parolni yangilash"}
              </button>
            </div>
          </div> */}

          {/* AI ключи */}
          {isTrainer && (
            <div className={s.card}>
              <div className={s.cardHeader}>
                <div className={s.cardTitle}>{t('meningAI')}</div>
                <div className={s.cardHint}>{t('tasdiqlashMal')}</div>
              </div>
              <div className={s.keysList}>
                {aikeys.length ? aikeys.map(k => (
                  <div key={k.id} className={s.keyItem}>
                    <div className={s.keyName}>{k.name || `Key #${k.id}`}</div>
                    <div className={s.keyVal}>{maskKey(k.key || k.value)}</div>
                  </div>
                )) : <div className={s.muted}>{t('kalitTopilmadi')}</div>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}